/**
 * Daten der Nachweise-Seite eines Antrags.
 *
 * Gelesen wird über den projektweiten Endpunkt und auf den Antrag gefiltert – bei den üblichen
 * ein bis drei Anträgen eines Projekts lohnt kein eigener Abruf je Antrag. Geschrieben wird über
 * die antragsbezogenen Pfade; die Antrags-ID steht in jeder Zeile. Die benötigenden
 * Antragsbausteine nennt jeder Nachweis selbst.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { permitsApi } from "../../../../services/api/permits";
import { parseApiError } from "../../../../services/api/errorPresentation";
import { useSaveState } from "../../../../hooks/useSaveState";
import { hasAnyFieldChanged } from "../../../../utils/formUtils";
import {
  Evidence,
  EvidenceCreate,
  EvidenceHistorieEintrag,
  EvidenceUebergang,
  EvidenceUpdate,
  PermitProcedure,
  ProjectEvidence,
  ProjectPermitCostItem,
} from "../../../../types";
import { EvidenceCostData, useEvidenceCostSaver } from "./useEvidenceCostSaver";
import { bausteinTypIds } from "../sections/bausteinOptionen";

interface UseProjectEvidencesProps {
  projectId: string | undefined;
  /** Der Antrag, dessen Nachweise die Seite zeigt */
  procedureId: string;
  /**
   * Leserecht an „Genehmigung › Kosten“ – ohne es lädt die Seite keine Kostenpositionen.
   * `undefined`, solange die Rechte noch laden: Dann wartet der Abruf, statt zweimal zu laden.
   */
  mayReadCosts: boolean | undefined;
}

/**
 * Ob ein Teil-Update etwas am Nachweis ändert. Die Dokumente sind eine Liste mit Reihenfolge und
 * werden als Ganzes verglichen, alle übrigen Felder skalar.
 */
const nachweisHatAenderung = (
  evidence: Evidence,
  data: EvidenceUpdate,
): boolean => {
  const { dokumente, application_component_type_ids, ...skalare } = data;
  if (
    application_component_type_ids !== undefined &&
    JSON.stringify([...application_component_type_ids].sort()) !==
      JSON.stringify(bausteinTypIds(evidence).sort())
  ) {
    return true;
  }
  if (
    dokumente !== undefined &&
    JSON.stringify(dokumente ?? []) !== JSON.stringify(evidence.dokumente ?? [])
  ) {
    return true;
  }
  return hasAnyFieldChanged(evidence, skalare);
};

export function useProjectEvidences({
  projectId,
  procedureId,
  mayReadCosts,
}: UseProjectEvidencesProps) {
  const [evidences, setEvidences] = useState<ProjectEvidence[]>([]);
  const [costItems, setCostItems] = useState<ProjectPermitCostItem[]>([]);
  const [procedures, setProcedures] = useState<PermitProcedure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Speicher-Zustand aller Schreibvorgänge dieser Seite; PageTemplate macht daraus den Toast.
  const { isSaving, saveError, speichern } = useSaveState();

  const load = useCallback(async () => {
    if (!projectId || mayReadCosts === undefined) return;
    setLoading(true);
    setError(null);
    try {
      const [evidencesData, costItemsData, proceduresData] = await Promise.all([
        permitsApi.getProjectEvidences(projectId),
        mayReadCosts
          ? permitsApi.getProjectCostItems(projectId)
          : Promise.resolve([]),
        permitsApi.getPermitProcedures(projectId),
      ]);
      setEvidences(evidencesData ?? []);
      setCostItems(costItemsData ?? []);
      setProcedures(proceduresData ?? []);
    } catch (err) {
      setError(parseApiError(err).message);
      setEvidences([]);
      setCostItems([]);
      setProcedures([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, mayReadCosts]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Legt einen Nachweis am gewählten Antrag an – ohne Baustein; zugeordnet wird auf der Seite
   * "Anträge" (ADR 006). Den Anzeigenamen des Antrags setzt der Client wie der Server: die
   * Bezeichnung, sonst die Antragsart.
   */
  const createEvidence = useCallback(
    async (
      permitProcedureId: string,
      data: EvidenceCreate,
    ): Promise<ProjectEvidence | null> => {
      if (!projectId) return null;
      return speichern(async () => {
        const erstellt = await permitsApi.createEvidence(
          projectId,
          permitProcedureId,
          data,
        );
        const antrag = procedures.find(
          (procedure) => procedure.id === permitProcedureId,
        );
        const eintrag: ProjectEvidence = {
          ...erstellt,
          permit_procedure_bezeichnung:
            antrag?.bezeichnung || antrag?.procedure_type.name || "",
        };
        setEvidences((prev) => [...prev, eintrag]);
        return eintrag;
      });
    },
    [projectId, procedures, speichern],
  );

  /**
   * Speichert geänderte Felder eines Nachweises – aus dem Detail beim Verlassen eines Feldes und
   * aus den Zeilen der Tabelle. Mit Dirty-Check (ADR 004): Durchtabben ohne Änderung schickt
   * nichts und meldet nichts.
   */
  const updateEvidence = useCallback(
    async (evidenceId: string, data: EvidenceUpdate): Promise<void> => {
      const evidence = evidences.find((kandidat) => kandidat.id === evidenceId);
      if (!projectId || !evidence) return;
      if (!nachweisHatAenderung(evidence, data)) return;
      await speichern(async () => {
        const aktualisiert = await permitsApi.updateEvidence(
          projectId,
          evidence.permit_procedure_id,
          evidenceId,
          data,
        );
        // Den Antragsnamen liefert nur der Übersichts-Endpunkt; er bleibt unverändert, weil
        // dieses Update den Antrag nicht berührt.
        setEvidences((prev) =>
          prev.map((kandidat) =>
            kandidat.id === evidenceId
              ? {
                  ...kandidat,
                  ...aktualisiert,
                  permit_procedure_bezeichnung:
                    kandidat.permit_procedure_bezeichnung,
                }
              : kandidat,
          ),
        );
      });
    },
    [projectId, evidences, speichern],
  );

  /**
   * Führt eine Aktion am Nachweis aus (ADR 007). Liefert den Nachweis danach, im Fehlerfall
   * `null` – der Dialog bleibt dann offen, die Meldung zeigt PageTemplate.
   */
  const transitionEvidence = useCallback(
    async (
      evidenceId: string,
      data: EvidenceUebergang,
    ): Promise<Evidence | null> => {
      const evidence = evidences.find((kandidat) => kandidat.id === evidenceId);
      if (!projectId || !evidence) return null;
      return speichern(async () => {
        const aktualisiert = await permitsApi.uebergangEvidence(
          projectId,
          evidence.permit_procedure_id,
          evidenceId,
          data,
        );
        setEvidences((prev) =>
          prev.map((kandidat) =>
            kandidat.id === evidenceId
              ? {
                  ...kandidat,
                  ...aktualisiert,
                  permit_procedure_bezeichnung:
                    kandidat.permit_procedure_bezeichnung,
                }
              : kandidat,
          ),
        );
        // Plan- bzw. Istkosten aus dem Dialog: Die Position kann dabei erst entstanden sein.
        if (data.costs) {
          setCostItems(await permitsApi.getProjectCostItems(projectId));
        }
        return aktualisiert;
      });
    },
    [projectId, evidences, speichern],
  );

  /** Nachweishistorie für das Detail, neueste zuerst */
  const loadEvidenceHistorie = useCallback(
    async (evidence: Evidence): Promise<EvidenceHistorieEintrag[]> =>
      projectId
        ? permitsApi.getEvidenceHistorie(
            projectId,
            evidence.permit_procedure_id,
            evidence.id,
          )
        : [],
    [projectId],
  );

  const deleteEvidence = useCallback(
    async (evidenceId: string): Promise<void> => {
      const evidence = evidences.find((kandidat) => kandidat.id === evidenceId);
      if (!projectId || !evidence) throw new Error("Nachweis nicht gefunden");
      await permitsApi.deleteEvidence(
        projectId,
        evidence.permit_procedure_id,
        evidenceId,
      );
      setEvidences((prev) =>
        prev.filter((kandidat) => kandidat.id !== evidenceId),
      );
    },
    [projectId, evidences],
  );

  const createCost = useCallback(
    async (
      evidenceId: string,
      data: EvidenceCostData,
    ): Promise<ProjectPermitCostItem> => {
      const evidence = evidences.find((kandidat) => kandidat.id === evidenceId);
      if (!projectId || !evidence) throw new Error("Nachweis nicht gefunden");
      const erstellt = await permitsApi.createCostItem(
        projectId,
        evidence.permit_procedure_id,
        { evidence_id: evidenceId, ...data },
      );
      // Der Antragsname kommt sonst nur vom projektweiten Endpunkt; hier steht er am Nachweis.
      const eintrag: ProjectPermitCostItem = {
        ...erstellt,
        permit_procedure_bezeichnung: evidence.permit_procedure_bezeichnung,
      };
      setCostItems((prev) => [...prev, eintrag]);
      return eintrag;
    },
    [projectId, evidences],
  );

  const updateCost = useCallback(
    async (
      costItem: ProjectPermitCostItem,
      data: EvidenceCostData,
    ): Promise<ProjectPermitCostItem> => {
      if (!projectId) throw new Error("Nachweis nicht gefunden");
      const aktualisiert = await permitsApi.updateCostItem(
        projectId,
        costItem.permit_procedure_id,
        costItem.id,
        data,
      );
      const eintrag: ProjectPermitCostItem = {
        ...costItem,
        ...aktualisiert,
        permit_procedure_bezeichnung: costItem.permit_procedure_bezeichnung,
      };
      setCostItems((prev) =>
        prev.map((item) => (item.id === costItem.id ? eintrag : item)),
      );
      return eintrag;
    },
    [projectId],
  );

  const saveCost = useEvidenceCostSaver({
    costItems,
    createCostItem: createCost,
    updateCostItem: updateCost,
    speichern,
  });

  const nachweiseDesAntrags = useMemo(
    () =>
      evidences.filter(
        (evidence) => evidence.permit_procedure_id === procedureId,
      ),
    [evidences, procedureId],
  );
  const kostenDesAntrags = useMemo(
    () => costItems.filter((item) => item.permit_procedure_id === procedureId),
    [costItems, procedureId],
  );
  const antrag = useMemo(
    () => procedures.find((procedure) => procedure.id === procedureId) ?? null,
    [procedures, procedureId],
  );

  return {
    evidences: nachweiseDesAntrags,
    costItems: kostenDesAntrags,
    /** Der Antrag der Seite; null, wenn es ihn (nicht mehr) gibt */
    antrag,
    loading,
    error,
    isSaving,
    saveError,
    reload: load,
    createEvidence,
    updateEvidence,
    transitionEvidence,
    loadEvidenceHistorie,
    deleteEvidence,
    saveCost,
  };
}
